import { cn } from '@/lib/cn'
import { layoutRows, type Answers, type Question } from '@/lib/questionnaire'

/**
 * A questionnaire laid out as the couple sees it, read only: the preview in
 * the template editor, or, with `answers`, what a couple answered.
 */
export function QuestionnaireForm({
  name,
  intro,
  questions,
  answers,
  onOpenFile,
}: {
  name: string
  intro: string | null
  questions: Question[]
  answers?: Answers
  onOpenFile?: (fileId: string) => void
}) {
  return (
    <div className="mx-auto flex max-w-[44rem] flex-col gap-6">
      <header className="flex flex-col gap-3">
        <h2 className="text-[length:var(--text-xl)] font-semibold">{name || 'Untitled questionnaire'}</h2>
        {intro?.trim() ? (
          <p className="whitespace-pre-line text-[length:var(--text-sm)] leading-relaxed">{intro.trim()}</p>
        ) : null}
      </header>
      {layoutRows(questions).map((row) => (
        <div key={row.map((question) => question.id).join('+')} className={cn('grid gap-6', row.length === 2 && 'sm:grid-cols-2')}>
          {row.map((question) => (
            <PreviewQuestion
              key={question.id}
              question={question}
              {...(answers ? { answer: answers[question.id] ?? null } : {})}
              {...(onOpenFile ? { onOpenFile } : {})}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

const BOX = 'w-full rounded-md border border-[var(--border-hairline)] bg-[var(--surface-card)] px-3 text-[length:var(--text-sm)] text-taupe'

function PreviewQuestion({
  question,
  answer,
  onOpenFile,
}: {
  question: Question
  /** Present when showing answers; null is "not answered". */
  answer?: Answers[string] | null
  onOpenFile?: (fileId: string) => void
}) {
  const showing = answer !== undefined
  if (question.type === 'note') {
    return (
      <div className="flex flex-col gap-1">
        {question.label ? <p className="font-semibold">{question.label}</p> : null}
        {question.help ? <p className="whitespace-pre-line text-[length:var(--text-sm)]">{question.help}</p> : null}
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-2">
      <p className="text-[length:var(--text-sm)] font-semibold leading-snug">
        {question.label || 'Untitled question'}
        {question.required ? <span className="text-clay"> *</span> : null}
      </p>
      {question.help ? <p className="-mt-1 text-[length:var(--text-2xs)] text-taupe">{question.help}</p> : null}
      {showing && question.type !== 'choice' ? (
        Array.isArray(answer) ? (
          answer.length ? (
            <ul className="flex flex-col gap-1 text-[length:var(--text-sm)]">
              {answer.map((file) => (
                <li key={file.id}>
                  {onOpenFile ? (
                    <button type="button" className="underline underline-offset-4" onClick={() => onOpenFile(file.id)}>
                      {file.name}
                    </button>
                  ) : (
                    file.name
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[length:var(--text-sm)] text-taupe">No files</p>
          )
        ) : answer ? (
          <p className="whitespace-pre-line text-[length:var(--text-sm)]">{answer}</p>
        ) : (
          <p className="text-[length:var(--text-sm)] text-taupe">Not answered</p>
        )
      ) : null}
      {!showing && question.type === 'short' ? <div className={cn(BOX, 'flex h-9 items-center')}>Your answer</div> : null}
      {!showing && question.type === 'long' ? <div className={cn(BOX, 'h-24 py-2')}>Your answer</div> : null}
      {!showing && question.type === 'file' ? <div className={cn(BOX, 'flex h-11 items-center gap-2')}>＋ Add photos or a file</div> : null}
      {question.type === 'choice' ? (
        <ul className="flex flex-col gap-1.5">
          {(question.options ?? []).filter(Boolean).map((option) => (
            <li
              key={option}
              className={cn('flex items-center gap-2.5 text-[length:var(--text-sm)]', showing && answer !== option && 'text-taupe')}
            >
              <span
                className={cn(
                  'size-4 shrink-0 rounded-full border border-[var(--color-rule-strong)]',
                  showing && answer === option && 'border-[5px] border-accent',
                )}
                aria-hidden
              />
              {option}
            </li>
          ))}
          {showing && !answer ? <li className="text-[length:var(--text-sm)] text-taupe">Not answered</li> : null}
        </ul>
      ) : null}
    </div>
  )
}
